/**
 * Joins a state machine to a loaded fighter: entering a state plays its clip,
 * and a finished clip tells the machine its animation is over.
 *
 * The machine stays pure — it never imports three, and it is tested without
 * one. This module is the only place the two layers meet.
 */

export function bindAnimation(machine, body, { fade = 0.18 } = {}) {
  let fighter = body;
  let clipMissing = false;
  /** The clip actually on the rig, which is not always the state's own. */
  let playing = null;
  /** A clip to use for the next state entry, in place of the state's. */
  let pending = null;

  function play(stateName, fadeTime, restart = false) {
    const state = machine.get(stateName);
    const clip = pending ?? state.clip;
    pending = null;
    // A state may set its own blend. A flinch is over in a quarter of a second,
    // and the default fade would still be ramping it in when the clip ends.
    const blend = fadeTime === 0 ? 0 : (state.fade ?? fadeTime);
    const played = fighter.play(clip, {
      loop: state.loop !== false,
      fade: blend,
      restart,
    });
    clipMissing = !played;
    playing = played ? clip : null;
    return played;
  }

  play(machine.state, 0);

  // Matched against what is *playing* rather than against the state's own
  // clip, because a move brings its own: the jab, the hook and the spinning
  // kick all run in `strike`, and only one of them is what the graph names.
  const listen = () =>
    fighter.animator?.onFinished((clipName) => {
      if (clipName === playing) machine.clipEnded();
    }) ?? null;
  let offFinished = listen();

  const unsubscribes = [
    // Re-entering a state restarts its clip. The graph only lists a state in
    // its own `to` for cases where that is exactly what is wanted — a second
    // blow restarting a flinch, a combo cancelling one punch into the next —
    // and without the restart the mixer would leave the old action running and
    // the new move would play out the tail of the old one.
    machine.onChange(({ from, to }) => play(to, fade, from === to)),
  ].filter(Boolean);

  return {
    /** True when the current state's clip is not in the fighter's library. */
    get clipMissing() {
      return clipMissing;
    },

    /** The clip on the rig right now — the move's, where a move named one. */
    get clip() {
      return playing;
    },

    /**
     * The clip the *next* state entry plays, in place of the one the graph
     * names. This is how a move brings its own animation: `strike` is a lead
     * jab, a hook or a capoeira kick depending on what was thrown, and the
     * state graph has no business knowing which.
     *
     * Set before the transition is requested, so the clip arrives with the
     * state change rather than a frame behind it — and consumed by that one
     * entry, so a state entered for any other reason is back to its own clip.
     */
    nextClip(clip) {
      pending = clip ?? null;
    },

    /** Clips the state graph asks for that the fighter cannot play. */
    missingClips() {
      const have = new Set(fighter.clipNames());
      return machine
        .states()
        .map((name) => machine.get(name).clip)
        .filter((clip, i, all) => !have.has(clip) && all.indexOf(clip) === i);
    },

    update(dt) {
      machine.update(dt);
      fighter.update(dt);
    },

    /**
     * Points the binding at a new body: the slot changed who is standing in
     * it. The machine is the same machine — same state, same listeners — so
     * only the animator side moves: the clip-end listener comes off the old
     * animator and goes on the new one, and the state the fighter is in starts
     * playing on the new body at once. The binding object itself does not
     * change, so anything that took it at boot (the debug overlay does) is
     * still holding the right one.
     */
    rebind(next) {
      offFinished?.();
      fighter = next;
      offFinished = listen();
      playing = null;
      play(machine.state, 0, true);
    },

    dispose() {
      for (const off of unsubscribes) off();
      offFinished?.();
      offFinished = null;
    },
  };
}
